import { useContext } from 'react'

import {
  Button,
  EntityMultiSelect,
  Form,
  ModalContext,
  SectionDivider,
  SidebarModal,
} from '@domain-group/suit-theme'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useBulkCreateAdAccountGroupLinks from './hooks/useBulkCreateAdAccountGroupLinks'

const MODAL_KEY = 'BulkCreateAdAccountGroupLinksModal'

type ModalCallbacks = {
  bulkCreateAdAccountGroupLinks: (payload: HandleCallbackActionParams) => void,
  closeModal: () => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const BulkCreateAdAccountGroupLinksModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { bulkCreateAdAccountGroupLinks, closeModal } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = modalData?.[MODAL_KEY] ?? {}
  const adAccountGroupLink = modalPayload.adAccountGroupLink ?? {}

  const {
    callbacks: {
      bulkCreateAdAccountGroupLinks: bulkCreateFn,
      loadMore,
      setSelectedIds,
      setState,
    },
    canLoadMore,
    creating,
    entities,
    entityName,
    entityParams,
    filteredEntityIds,
    filteredEntityIdsCount,
    isShowingClients,
    loading,
    saveEnabled,
    selectedIds,
  } = useBulkCreateAdAccountGroupLinks({ adAccountGroupLink })

  const bulkCreateAdAccountGroupLinksPayload = {
    callbacks: {
      action: bulkCreateFn,
      afterAction: closeModal,
    },
    entityParams,
    toastText: isShowingClients ? 'Linked Organisations' : 'Linked Users',
  }

  const buttonText = isShowingClients ? 'Link Organisations' : 'Link Users'

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title="Link"
        titleSecondLine={isShowingClients ? 'Organisations' : 'Users'}
      />

      <SidebarModal.Content>
        <Form.Field label={isShowingClients ? 'Search Organisations' : 'Search Users'}>
          <input onChange={e => setState({ entityName: e.target.value })} value={entityName} />
        </Form.Field>

        <SectionDivider>Multi Select Values</SectionDivider>

        <EntityMultiSelect
          callbacks={{
            loadMore,
            setSelectedIds,
          }}
          canLoadMore={canLoadMore}
          entities={entities}
          entityCount={filteredEntityIdsCount}
          entityTitleKey="name"
          filteredEntityIds={filteredEntityIds}
          loading={loading}
          selectedIds={selectedIds}
        />
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating}
          loadingBubbleColor="white"
          onClick={() => bulkCreateAdAccountGroupLinks(bulkCreateAdAccountGroupLinksPayload)}
          size="large"
        >
          {saveEnabled ? buttonText : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <BulkCreateAdAccountGroupLinksModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
